import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useLineageStore } from '@/stores/lineage';
import type { LineageNode } from '@/services/api';

function timeAgo(isoStr?: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const STATUS_DOT: Record<string, string> = {
  completed: 'bg-emerald-500',
  executing: 'bg-brand-teal',
  ready: 'bg-amber-400',
  deferred: 'bg-slate-400',
  saturated: 'bg-rose-400',
  failed: 'bg-rose-500',
  dispatched: 'bg-blue-400',
};

export function LineageRootList() {
  const navigate = useNavigate();
  const { roots, selectedRoot, filters, loading, setFilters, fetchRoots, selectRoot } =
    useLineageStore();

  useEffect(() => {
    fetchRoots();
  }, [fetchRoots]);

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="p-3 space-y-2 border-b border-slate-100">
        <input
          type="text"
          placeholder="Client ID (optional)"
          value={filters.clientId}
          onChange={(e) => setFilters({ clientId: e.target.value })}
          onBlur={() => fetchRoots()}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30 placeholder-slate-400"
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && roots.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">Loading...</div>
        ) : roots.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            No lineage roots.
            <p className="mt-1 text-xs">
              Roots appear once the autonomous fleet has dispatched at least one
              research chain.
            </p>
          </div>
        ) : (
          roots.map((r) => (
            <RootRow
              key={r.id}
              root={r}
              selected={selectedRoot?.id === r.id}
              onClick={() => {
                selectRoot(r);
                navigate(`/knowledge/estate/lineage/${r.id}`);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function RootRow({
  root,
  selected,
  onClick,
}: {
  root: LineageNode;
  selected: boolean;
  onClick: () => void;
}) {
  const statusClass = STATUS_DOT[root.status] ?? 'bg-slate-300';
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors',
        selected && 'bg-slate-50 border-l-2 border-l-brand-teal',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', statusClass)} />
        <span className="text-sm font-medium text-slate-800 truncate">
          {root.label || root.research_type || root.target_entity_name || 'Research chain'}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 pl-3.5">
        {root.research_type && (
          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
            {root.research_type}
          </span>
        )}
        {root.client_id && (
          <span className="truncate max-w-[6rem]">{root.client_id}</span>
        )}
        <span className="ml-auto">{timeAgo(root.created_at)}</span>
      </div>
    </button>
  );
}
